/** Signed-in user in the top-right corner: avatar + name; the menu shows the email, the membership, "Giới thiệu bạn
 * bè" (when the programme is on), "Quản lý" for operators and "Đăng xuất". */
import { useEffect, useId, useRef, useState } from "react";
import { REFERRAL } from "../messages";
import { initials, type Profile } from "../profile";

function Avatar({ profile, large = false }: { profile: Profile | null; large?: boolean }) {
  const [broken, setBroken] = useState(false);
  const cls = `avatar${large ? " avatar-lg" : ""}`;
  if (profile?.picture && !broken) {
    // Google's image host refuses some requests that carry a Referer
    return <img className={cls} src={profile.picture} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
  }
  if (profile) return <span className={cls} aria-hidden="true">{initials(profile.name)}</span>;
  return (
    <span className={cls} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6z" />
      </svg>
    </span>
  );
}

interface Props {
  profile: Profile | null;
  onLogout: () => void;
  /** "Gói Plus · đến 21/11/2026" (messages.tierLine) */
  tier?: string | null;
  isAdmin?: boolean;
  /** v4 "Giới thiệu bạn bè" (opens components/ReferralDialog.tsx); absent = programme off, no item */
  onReferral?: () => void;
}

export function AccountMenu({ profile, onLogout, tier, isAdmin, onReferral }: Props) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const name = profile?.name ?? "Tài khoản";

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="account" ref={box}>
      <button ref={button} type="button" className="account-btn" aria-label={`Tài khoản: ${name}`} aria-expanded={open}
              aria-controls={menuId} onClick={() => setOpen((v) => !v)}>
        <Avatar profile={profile} />
        <span className="account-name">{name}</span>
        <span className="account-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div id={menuId} className="account-menu">
          <div className="account-head">
            <Avatar profile={profile} large />
            <div>
              <strong>{name}</strong>
              {profile?.email && <small className="muted">{profile.email}</small>}
              {tier && <small className="tier-line">{tier}</small>}
            </div>
          </div>
          {onReferral && (
            <button type="button" className="btn btn-small account-referral" onClick={() => { setOpen(false); onReferral(); }}>
              {REFERRAL.menu}
            </button>
          )}
          <div className="actions">
            {isAdmin && <a className="btn btn-small" href="/admin">Quản lý</a>}
            <button type="button" className="btn btn-small" onClick={() => { setOpen(false); onLogout(); }}>Đăng xuất</button>
          </div>
        </div>
      )}
    </div>
  );
}
